'use client'

import { useEffect, useState, useRef } from 'react'
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
} from '@headlessui/react'
import {
    ArrowPathIcon,
  Bars3Icon,
  BellIcon,
  BoltIcon,
  CalendarIcon,
  ChartBarIcon,
  ChartBarSquareIcon,
  ChartPieIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  FolderIcon,
  ForwardIcon,
  HomeIcon,
  PlayCircleIcon,
  PlusIcon,
  TagIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { ChevronDownIcon, MagnifyingGlassIcon, UserCircleIcon } from '@heroicons/react/20/solid'
import { useNavigate } from 'react-router-dom'
import { ActionTypes } from '../../Store/Stores/User'
import { ActionTypes as AppActionTypes } from '../../Store'
import { useAPI } from '../../API'
import { useAppDispatch, useAppState } from '../../Store'
import { useTranslation } from '../../Translation'
import { color, groupHoverTextColor, hoverTextColor, textColor } from '../../Resources/CSS/styling'
import Modal, { useModal } from '../Modal'
import { logo, logoWithName } from '../../Resources/Images'
import { TextInput } from '../Inputs/TextInput'
import { Button } from '../Buttons'
import { useRefresh } from '../../Hooks/useRefresh'


function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function SidebarNav({ children, currentPage, breadcrumb, contentPadding = true }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [navigation, setNavigation] = useState([]);
  const timeoutRef = useRef(null);

  const api = useAPI();
  const dispatch = useAppDispatch();
  const appState = useAppState();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const { sidebarSearch } = appState;
  const { user } = appState;
  const [search, setSearch] = useState(sidebarSearch);
  const [boardsOptions, setBoardsOptions] = useState([]);
  const [boardModalOpen, setBoardModalOpen] = useState(false);
  const { refreshId, refresh } = useRefresh();

  useEffect(() => {
    setSearch(sidebarSearch);
  }, [sidebarSearch]);

  const { translate, language, setLanguage } = useTranslation();

  useEffect(() => {
    const fetchBoards = async () => {
      const boards = await api.getBoards();
      setBoardsOptions(boards.data.boards);
    }
    fetchBoards();
  }, [refreshId]);

  useEffect(() => {
    setNavigation([
        // { name: translate('sidebar.dashboard'), href: '/dashboard', icon: HomeIcon, current: currentPage === 'dashboard' },
        { name: translate('sidebar.events'), href: '/events', icon: ChartPieIcon, current: currentPage === 'events' },
        { name: translate('sidebar.reports'), href: '/reports', icon: ChartBarSquareIcon, current: currentPage === 'reports' },
        { name: translate('sidebar.automatisation'), href: '/automatisations', icon: BoltIcon, current: currentPage === 'automatisations' },
        { name: translate('sidebar.campaigns'), href: '/campaigns', icon: PlayCircleIcon, current: currentPage === 'campaigns' },
        { name: translate('sidebar.contacts'), href: '/contacts', icon: UsersIcon, current: currentPage === 'contacts' },
        { name: translate('sidebar.tags'), href: '/tags', icon: TagIcon, current: currentPage === 'tags' },
        { name: translate('sidebar.scheduledTask'), href: '/scheduled-tasks', icon: ArrowPathIcon, current: currentPage === 'scheduledTask' },
        // { name: translate('sidebar.boards'), type: 'dynamic_content', icon: ChartBarIcon, current: currentPage === 'boards', isOpen: false, options: boardsOptions },
      ]);
  }, [language]);

  const logout = async () => {
    await api.logout();
    dispatch({ type: ActionTypes.LOGOUT });
    navigate('/login');
    localStorage.removeItem('automatisations_tags_filter');
    localStorage.removeItem('campaigns_tags_filter');
  }

  const userNavigation = [
    { name: translate('sidebar.profile'), href: '#' },
    { name: translate('sidebar.languageSwitch'), action: () => setLanguage(language === 'fr' ? 'en' : 'fr') },
    { name: translate('sidebar.logout'), action: logout },
  ]

  const handleSearch = (e) => {
    setSearch(e.target.value);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      dispatch({ type: AppActionTypes.SET_SIDEBAR_SEARCH, payload: e.target.value });
    }, 500);
  }

  return (
    <>
      {/*
        This example requires updating your template:

        ```
        <html class="h-full bg-white">
        <body class="h-full">
        ```
      */}
      <Modal 
        open={boardModalOpen}
        onClose={() => setBoardModalOpen(false)} 
        title={translate('boards.create')} 
        children={<BoardModal refresh={refresh} setOpen={setBoardModalOpen} open={boardModalOpen} />} />
      <div className="w-full h-full">
        <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                  <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon aria-hidden="true" className="size-6 text-white" />
                  </button>
                </div>
              </TransitionChild>
              {/* Sidebar component, swap this element with another sidebar if you like */}
              <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
                <div className="flex h-16 shrink-0 items-center">
                  <img
                    alt="Hoodle"
                    src={logoWithName}
                    className="h-8 w-auto"
                  />
                </div>
                <nav className="flex flex-1 flex-col">
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul role="list" className="-mx-2 space-y-1">
                        {navigation.map((item) => (
                          <li key={item.name}>
                            <a
                              onClick={() => {
                                dispatch({ type: AppActionTypes.SET_SIDEBAR_SEARCH, payload: "" });
                                setSearch("");
                                navigate(item.href);
                              }}
                              className={classNames(
                                item.current
                                  ? `bg-gray-50 ${textColor}`
                                  : `text-gray-700 hover:bg-gray-50 ${hoverTextColor}`,
                                'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold',
                              )}
                            >
                              <item.icon
                                aria-hidden="true"
                                className={classNames(
                                  item.current ? textColor : `text-gray-400 ${groupHoverTextColor}`,
                                  'size-6 shrink-0',
                                )}
                              />
                              {item.name}
                              {item.type === 'dynamic_content' && (
                                <ChevronDownIcon
                                aria-hidden="true"
                                  className="size-6 shrink-0"
                                />
                              )}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </li>
                    <li className='mt-4 mx-2'>
                      <div className='flex items-center gap-x-2 flex-row justify-between'>
                        <p className='text-gray-700 text-sm font-bold'>{translate('sidebar.boards')}</p>
                        <PlusIcon
                          aria-hidden="true"
                          className={`size-6 shrink-0 text-gray-700 hover:cursor-pointer ${hoverTextColor}`}
                          onClick={() => setBoardModalOpen(true)}
                        />
                      </div>
                      <ul role="list" className="space-y-4 mt-2">
                        {boardsOptions.map((option) => (
                          <li className='flex flex-row w-full items-center' key={option.name}>
                            <span className='bg-gray-50 border border-gray-200 h-8 w-8 rounded-md p-2 text-MD font-bold flex items-center justify-center'>{option.name.slice(0, 1)}</span>
                            <a onClick={() => navigate(`/board/${option.id}`)} className={`${hoverTextColor} font-bold text-sm flex items-center gap-x-2 ml-2 hover:cursor-pointer`}>{option.name}</a>
                          </li>
                        ))}
                      </ul>
                    </li>
                    <li className="mt-auto">
                      <a
                        href="/settings"
                        className={`group -mx-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-gray-700 hover:bg-gray-50 ${hoverTextColor}`}
                      >
                        <Cog6ToothIcon
                          aria-hidden="true"
                          className={`size-6 shrink-0 text-gray-400 ${groupHoverTextColor}`}
                        />
                        {translate('sidebar.settings')}
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-40 lg:flex lg:w-72 lg:flex-col">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4">
            <div className="flex h-16 shrink-0 items-center">
              <img
                alt="Hoodle"
                src={logoWithName}
                className="h-8 w-auto"
              />
            </div>
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {navigation.map((item) => (
                      <li className={item.current
                        ? `bg-gray-50 ${textColor}`
                        : `text-gray-700 hover:bg-gray-50`} key={item.name}>
                        <a
                          onClick={() => {
                            dispatch({ type: AppActionTypes.SET_SIDEBAR_SEARCH, payload: "" });
                            setSearch("");
                            if (item.type === 'dynamic_content') {
                              setNavigation(navigation.map((n) => ({ ...n, isOpen: n.name === item.name ? !n.isOpen : n.isOpen })));
                            } else {
                              navigate(item.href);
                            }
                          }}
                          className={classNames(
                            item.current
                              ? `bg-gray-50 ${textColor}`
                              : `text-gray-700 hover:bg-gray-50 ${hoverTextColor}`,
                            'group flex gap-x-3 rounded-md p-2 text-md font-semibold cursor-pointer',
                          )}
                        >
                          <item.icon
                            aria-hidden="true"
                            className={classNames(
                              item.current ? textColor : `text-gray-400 ${groupHoverTextColor}`,
                              'size-6 shrink-0',
                            )}
                          />
                          {item.name}
                          {item.type === 'dynamic_content' &&
                          <div className="flex items-center gap-x-2 justify-end w-full">
                            {item.isOpen ? (
                              <ChevronUpIcon
                                aria-hidden="true"
                                className="size-6 shrink-0"
                              />
                            ) : (
                              <ChevronDownIcon
                                aria-hidden="true"
                                className="size-6 shrink-0"
                              />
                            )}

                            <PlusIcon
                              aria-hidden="true"
                              className={`size-6 shrink-0 text-gray-700 ${hoverTextColor}`}
                            />
                          </div>}
                        </a>
                        {item.type === 'dynamic_content' && item.isOpen && (
                          <ul role="list" className="mx-12 px-2 space-y-1">
                            {item.options.map((option) => (
                              <li key={option.name}>
                                <a className={`${hoverTextColor} font-bold text-sm`} href={option.href}>{option.name}</a>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                </li>
                <li className='mt-4 mx-2'>
                  <div className='flex items-center gap-x-2 flex-row justify-between'>
                    <p className='text-gray-700 text-sm font-bold'>{translate('sidebar.boards')}</p>
                    <PlusIcon
                      aria-hidden="true"
                      className={`size-6 shrink-0 text-gray-700 hover:cursor-pointer ${hoverTextColor}`}
                      onClick={() => setBoardModalOpen(true)}
                    />
                  </div>
                  <ul role="list" className="space-y-4 mt-2">
                    {boardsOptions.map((option) => (
                      <li className='flex flex-row w-full items-center' key={option.name}>
                        <span className='bg-gray-50 border border-gray-200 h-8 w-8 rounded-md p-2 text-MD font-bold flex items-center justify-center'>{option.name.slice(0, 1)}</span>
                        <a onClick={() => navigate(`/board/${option.id}`)} className={`${hoverTextColor} font-bold text-sm flex items-center gap-x-2 ml-2 hover:cursor-pointer`}>{option.name}</a>
                      </li>
                    ))}
                  </ul>
                </li>
                <li className="mt-auto">
                  <a
                    onClick={() => {
                      dispatch({ type: AppActionTypes.SET_SIDEBAR_SEARCH, payload: "" });
                      setSearch("");
                      navigate('/settings');
                    }}
                    className={`${currentPage === 'settings' ? 'bg-gray-50 ' + textColor : 'text-gray-700 hover:bg-gray-50 ' + hoverTextColor} group -mx-2 flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold cursor-pointer`}
                  >
                    <Cog6ToothIcon
                      aria-hidden="true"
                      className={`size-6 shrink-0 ${currentPage === 'settings' ? textColor : 'text-gray-400 ' + groupHoverTextColor}`}
                    />
                    {translate('sidebar.settings')}
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <div className="lg:pl-72 w-full h-full">
          <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
            <button type="button" onClick={() => setSidebarOpen(true)} className="-m-2.5 p-2.5 text-gray-700 lg:hidden">
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon aria-hidden="true" className="size-6" />
            </button>

            {/* Separator */}
            <div aria-hidden="true" className="h-6 w-px bg-gray-200 lg:hidden" />

            <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
              <form action="#" method="GET" className="grid flex-1 grid-cols-1">
                <input
                  name="search"
                  type="search"
                  placeholder="Search"
                  aria-label="Search"
                  value={search}
                  onChange={handleSearch}
                  className="col-start-1 row-start-1 block size-full bg-white pl-8 text-base text-gray-900 outline-none border-0 placeholder:text-gray-400 sm:text-sm/6"
                />
                <MagnifyingGlassIcon
                  aria-hidden="true"
                  className="pointer-events-none col-start-1 row-start-1 size-5 self-center text-gray-400"
                />
              </form>
              <div className="flex items-center gap-x-4 lg:gap-x-6">
                {/* <button type="button" className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500">
                  <span className="sr-only">View notifications</span>
                  <BellIcon aria-hidden="true" className="size-6" />
                </button> */}

                {/* Separator */}
                <div aria-hidden="true" className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200" />

                {/* Profile dropdown */}
                <Menu as="div" className="relative">
                  <MenuButton className="-m-1.5 flex items-center p-1.5">
                    <span className="sr-only">Open user menu</span>
                    <UserCircleIcon className="size-10" />
                    <span className="hidden lg:flex lg:items-center">
                      <span aria-hidden="true" className="ml-4 text-sm/6 font-semibold text-gray-900">
                        {user?.first_name} {user?.last_name}
                      </span>
                      <ChevronDownIcon aria-hidden="true" className="ml-2 size-5 text-gray-400" />
                    </span>
                  </MenuButton>
                  <MenuItems
                    transition
                    className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                  >
                    {userNavigation.map((item) => (
                      <MenuItem key={item.name}>
                        <a
                          onClick={item.action}
                          className="block px-3 py-1 text-sm/6 text-gray-900 data-[focus]:bg-gray-50 data-[focus]:outline-none cursor-pointer"
                        >
                          {item.name}
                        </a>
                      </MenuItem>
                    ))}
                  </MenuItems>
                </Menu>
              </div>
            </div>
          </div>

          {breadcrumb}

          <main className={`${contentPadding ? 'py-10' : ''} w-full h-full bg-white min-h-screen`}>
            <div className={`${contentPadding ? 'px-4 sm:px-6 lg:px-8' : ''} h-full`}>
              {children}
            </div>
          </main>
        </div>
      </div>
    </>
  )
}

export const BoardModal = ({ refresh, setOpen, board, open }) => {

  const [name, setName] = useState(board ? board.name : '');
  const { translate } = useTranslation();
  const api = useAPI();

  const handleCreate = async () => {
      try {
          await api.createBoard(name);
          refresh();

          setOpen(false);
          setName('');
      } catch (error) {
          console.error(error);
      }
  }

  const handleUpdate = async () => {
      try {
          await api.updateBoard(board.id, name);
          refresh();
          setOpen(false);
      } catch (error) {
          console.error(error);
      }
  }

  useEffect(() => {
      if (!open) {
          setName('');
      }
  }, [open]);

  return (
      <div className="mt-4 overflow-visible">
          <TextInput id="name" name="name" label={translate('boards.name')} placeholder={translate('boards.name_placeholder')} value={name} onChange={(e) => setName(e.target.value)} />
          <div className="flex justify-end mt-4">
              <Button disabled={!name || name.length === 0} onClick={board ? handleUpdate : handleCreate}>{board ? translate('update') : translate('create')}</Button>
          </div>
      </div>
  )
}
